import { Injectable } from "@angular/core";
import { BehaviorSubject } from "rxjs";

/**
* Interface representing a notification.
*/
export interface Notification {
  /** Type of notification. */
  type: "info" | "success" | "warning" | "error";
  /** The message to be displayed in the notification. */
  message: string;
  /** Call to action for the notification. */
  cta?: {
    /** Caption for the call to action. */
    caption: string;
    /** Type of the call to action, either a link or a button. */
    type: "link" | "button",
    /** Action to be performed when the call to action is clicked. */
    action: () => void
  };
  /** Icon class for the notification. */
  icon?: string;
  /** Flag to determine if the notification is dismissible. */
  dismissible?: boolean;
}

/**
 * Service to manage notifications from various data providing services.
 */
@Injectable({
  providedIn: "root",
})
export class NotificationManagerService {
  private notificationsBySubject: { [subject: string]: Notification[] } = {};
  /** Observable holding the current list of notifications. */
  private notificationsSubject = new BehaviorSubject<Notification[]>([]);
  /** Observable stream of notifications. */
  notifications$ = this.notificationsSubject.asObservable();

  /**
   * Updates the aggregated list of all notifications and emits them.
   * @private
   */
  private refreshNotifications(): void {
    const allNotifications = Object.values(this.notificationsBySubject).flat();
    this.notificationsSubject.next(allNotifications);
  }

  /**
   * Sets the notifications for a specific subject.
   * @param {string} subject - The subject name.
   * @param {Notification[]} notifications - Array of notifications for the subject.
   */
  setNotifications(subject: string, notifications: Notification[]): void {
    this.notificationsBySubject[subject] = notifications?.length ? [ ...notifications ] : [];
    this.refreshNotifications();
  }

  /**
   * Adds a notification for a specific subject.
   * @param {string} subject - The subject name.
   * @param {Notification} notification - The notification to add.
   */
  addNotification(subject: string, notification: Notification): void {
    if (!this.notificationsBySubject[subject]) {
      this.notificationsBySubject[subject] = [];
    }
    this.notificationsBySubject[subject].push(notification);
    this.refreshNotifications();
  }

  /**
   * Removes a notification.
   * @param {Notification} notification - The notification to remove.
   */
  removeNotification(notification: Notification): void {
    for (const subject of Object.keys(this.notificationsBySubject)) {
      this.notificationsBySubject[subject] = this.notificationsBySubject[subject].filter(n => n !== notification);
    }
    this.refreshNotifications();
  }
}
